// ============================================================================
// FACILITATOR DETAILS — expanded section under a row of the facilitator list.
// History is visible to Tier 1, system admins and the facilitator themselves;
// recording and deleting entries is system admin only.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Loader, AlertCircle, Plus } from 'lucide-react';
import {
  FacilitatorReportRow,
  FacilitatorTrainingEvent,
  FacilitatorTrainingEventType,
  AssessmentOutcome,
  TRAINING_EVENT_LABELS,
  listTrainingEvents,
  addTrainingEvent,
  deleteTrainingEvent
} from '../../services/studio-bookings';
import { SectorList, TrainingStages, TrainingHistory, formatDate } from './FacilitatorParts';

const today = () => new Date().toISOString().slice(0, 10);

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase">{title}</p>
      {children}
    </section>
  );
}

function RecordTrainingForm({ userId, onSaved }: { userId: string; onSaved: () => void }) {
  const [type, setType] = useState<FacilitatorTrainingEventType>('in_person_training');
  const [date, setDate] = useState(today());
  const [outcome, setOutcome] = useState<AssessmentOutcome>('pass');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await addTrainingEvent(userId, {
        type,
        date,
        ...(type === 'assessment' && { outcome }),
        ...(notes.trim() && { notes: notes.trim() })
      });
      setNotes('');
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record entry');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3.5 border border-[#e2e8f0] rounded-xl bg-white">
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex rounded-lg border border-[#e2e8f0] overflow-hidden">
        {(['in_person_training', 'assessment'] as FacilitatorTrainingEventType[]).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`flex-1 py-1.5 text-xs font-semibold ${type === t ? 'bg-navy text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
          >
            {TRAINING_EVENT_LABELS[t]}
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <label className="flex-1">
          <span className="block text-xs font-semibold text-navy mb-1">Date</span>
          <input
            type="date"
            required
            max={today()}
            value={date}
            onChange={e => setDate(e.target.value)}
            className="w-full border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 text-sm text-navy"
          />
        </label>
        {type === 'assessment' && (
          <label className="flex-1">
            <span className="block text-xs font-semibold text-navy mb-1">Outcome</span>
            <select
              value={outcome}
              onChange={e => setOutcome(e.target.value as AssessmentOutcome)}
              className="w-full border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 text-sm text-navy"
            >
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
            </select>
          </label>
        )}
      </div>
      <label className="block">
        <span className="block text-xs font-semibold text-navy mb-1">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          maxLength={2000}
          rows={2}
          className="w-full border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 text-sm text-navy"
        />
      </label>
      <button
        type="submit"
        disabled={saving || !date}
        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-dark disabled:opacity-60"
      >
        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Record {TRAINING_EVENT_LABELS[type].toLowerCase()}
      </button>
    </form>
  );
}

export default function FacilitatorDetails({
  row,
  canViewHistory,
  canEditTraining,
  actions,
  onTrainingChanged
}: {
  row: FacilitatorReportRow;
  canViewHistory: boolean;
  canEditTraining: boolean;
  actions?: React.ReactNode;
  onTrainingChanged: () => void;
}) {
  const [events, setEvents] = useState<FacilitatorTrainingEvent[]>([]);
  const [loading, setLoading] = useState(canViewHistory);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  function loadEvents() {
    if (!canViewHistory) return;
    setLoading(true);
    listTrainingEvents(row.userId)
      .then(setEvents)
      .catch(() => setError('Failed to load training history'))
      .finally(() => setLoading(false));
  }
  useEffect(loadEvents, [row.userId, canViewHistory]);

  function handleSaved() {
    loadEvents();
    onTrainingChanged();
  }

  async function handleDelete(event: FacilitatorTrainingEvent) {
    if (!window.confirm(`Delete this ${TRAINING_EVENT_LABELS[event.type].toLowerCase()} entry from ${formatDate(event.date)}?`)) return;
    setDeletingId(event.eventId);
    try {
      await deleteTrainingEvent(row.userId, event.eventId);
      handleSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete entry');
    } finally {
      setDeletingId(null);
    }
  }

  const { bookings, nextBooking } = row;

  return (
    <div className="px-5 py-5 bg-slate-50/60 space-y-5">
      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Section title="Sectors">
          <SectorList primary={row.primarySector} secondary={row.secondarySectors} />
        </Section>

        <Section title="Bookings delivered">
          <div className="grid grid-cols-2 gap-3">
            {([['Lead', bookings.leadAllTime, bookings.leadLast12m], ['Support', bookings.supportAllTime, bookings.supportLast12m]] as const)
              .map(([label, all, recent]) => (
                <div key={label} className="p-3 border border-[#e2e8f0] rounded-xl bg-white">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-xl font-bold text-navy">{all}</p>
                  <p className="text-xs text-slate-400">{recent} in last 12 months</p>
                </div>
              ))}
          </div>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-navy">Next booking: </span>
            {nextBooking
              ? `${formatDate(nextBooking.startDate)} · ${nextBooking.customer} · ${nextBooking.roomName} (${nextBooking.role})`
              : 'None scheduled'}
          </p>
        </Section>

        <Section title="Training">
          <TrainingStages training={row.training} />
        </Section>
      </div>

      {canViewHistory && (
        <Section title="Training history">
          <div className={`grid grid-cols-1 ${canEditTraining ? 'md:grid-cols-2' : ''} gap-4 items-start`}>
            {loading
              ? <div className="flex justify-center py-4"><Loader className="w-5 h-5 animate-spin text-slate-400" /></div>
              : <TrainingHistory events={events} deletingId={deletingId} onDelete={canEditTraining ? handleDelete : undefined} />}
            {canEditTraining && <RecordTrainingForm userId={row.userId} onSaved={handleSaved} />}
          </div>
        </Section>
      )}

      {actions && (
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#e2e8f0]">{actions}</div>
      )}
    </div>
  );
}
